import React, { useState, useEffect } from "react";
import { Transaction } from "../../types";

interface TransactionDetailsProps {
  isOpen: boolean;
  transaction: Transaction | null;
  onClose: () => void;
}

const TransactionDetails: React.FC<TransactionDetailsProps> = ({
  isOpen,
  transaction,
  onClose,
}) => {
  const [shouldShowPanel, setShouldShowPanel] = useState(false);

  // Handle slide animation
  useEffect(() => {
    if (isOpen) {
      // Add the open class on the next tick to trigger the transition
      const timer = setTimeout(() => {
        setShouldShowPanel(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setShouldShowPanel(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  if (!isOpen || !transaction) return null;

  const formatDate = (dateString: string) => {
    if (!dateString) return "Not specified";
    return new Date(dateString).toLocaleString();
  };

  const formatAmount = (amount: number, transType: string) => {
    const sign = transType === "sale" ? "+" : "-";
    return `${sign}$${Math.abs(amount).toFixed(2)}`;
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && <div className="slide-overlay" onClick={handleClose}></div>}

      {/* Slide Panel */}
      {isOpen && (
        <div
          className={`slide-panel ${shouldShowPanel ? "slide-panel-open" : ""}`}
        >
          <div className="slide-panel-header">
            <h2>📋 Transaction Details</h2>
            <button className="slide-panel-close" onClick={handleClose}>
              ✕
            </button>
          </div>

          <div className="slide-panel-content">
            <div className="settings-grid">
              {/* Transaction Overview */}
              <div className="setting-item full-width">
                <h3 style={{ margin: "0 0 1rem 0", color: "#374151" }}>
                  Transaction Overview
                </h3>
                <div
                  style={{
                    background: "#f8f9fa",
                    padding: "1rem",
                    borderRadius: "8px",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <span style={{ fontWeight: "600" }}>Invoice:</span>
                    <span>{transaction.invoice || "Not specified"}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <span style={{ fontWeight: "600" }}>Amount:</span>
                    <span
                      style={{
                        fontWeight: "600",
                        color:
                          transaction.transType === "sale"
                            ? "#10b981"
                            : "#ef4444",
                      }}
                    >
                      {formatAmount(transaction.amount, transaction.transType)}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <span style={{ fontWeight: "600" }}>Type:</span>
                    <span
                      className={`terminal-status-badge ${
                        transaction.transType === "sale"
                          ? "status-active"
                          : "status-inactive"
                      }`}
                    >
                      {transaction.transType === "sale" ? "Sale" : "Refund"}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <span style={{ fontWeight: "600" }}>Status:</span>
                    <span
                      className={`terminal-status-badge ${
                        transaction.status?.toLowerCase() === "success"
                          ? "status-active"
                          : transaction.status?.toLowerCase() === "fail"
                          ? "status-inactive"
                          : "status-pending"
                      }`}
                    >
                      {transaction.status || "Unknown"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Transaction Details */}
              <div className="setting-item">
                <label>Payment Identifier</label>
                <div className="form-input-readonly">
                  {transaction.id ||
                    "Not specified"}
                </div>
              </div>

              <div className="setting-item">
                <label>Transaction ID</label>
                <div className="form-input-readonly">
                  {transaction.transactionId ||
                    "Not specified"}
                </div>
              </div>
              
              {transaction.originalTransactionId && (
                <div className="setting-item">
                  <label>Original Transaction ID</label>
                  <div className="form-input-readonly">
                    {transaction.originalTransactionId}
                  </div>
                </div>
              )}

              <div className="setting-item">
                <label>Date & Time</label>
                <div className="form-input-readonly">
                  {formatDate(transaction.date)}
                </div>
              </div>

              <div className="setting-item">
                <label>Request Origin</label>
                <div className="form-input-readonly">
                  <span
                    style={{
                      padding: "0.25rem 0.5rem",
                      borderRadius: "4px",
                      fontSize: "0.75rem",
                      fontWeight: "500",
                      background: "#f3f4f6",
                      color: "#374151",
                      border: "1px solid #d1d5db",
                      textTransform: "uppercase",
                    }}
                  >
                    {transaction.requestOrigin || "Not specified"}
                  </span>
                </div>
              </div>

              <div className="setting-item">
                <label>Description</label>
                <div className="form-input-readonly">
                  {transaction.description || "Not specified"}
                </div>
              </div>

              {/* Payment Information */}
              <div className="setting-item full-width">
                <h3 style={{ margin: "1.5rem 0 1rem 0", color: "#374151" }}>
                  Payment Information
                </h3>
              </div>

              <div className="setting-item">
                <label>Card</label>
                <div className="form-input-readonly">
                  {transaction.card || "Not specified"}
                </div>
              </div>

              <div className="setting-item">
                <label>Entry Type</label>
                <div className="form-input-readonly">
                  <span
                    style={{
                      padding: "0.125rem 0.25rem",
                      borderRadius: "4px",
                      fontSize: "0.75rem",
                      fontWeight: "500",
                      background: "#dbeafe",
                      color: "#1e40af",
                    }}
                  >
                    {transaction.entryType || "Not specified"}
                  </span>
                </div>
              </div>

              <div className="setting-item">
                <label>Authorization Code</label>
                <div className="form-input-readonly">
                  {transaction.authCode || "Not specified"}
                </div>
              </div>

              <div className="setting-item">
                <label>Authorization Date</label>
                <div className="form-input-readonly">
                  {transaction.authDate}
                </div>
              </div>

              {transaction.tip > 0 && (
                <div className="setting-item">
                  <label>Tip Amount</label>
                  <div className="form-input-readonly">
                    ${transaction.tip.toFixed(2)}
                  </div>
                </div>
              )}

              {/* Account Information */}
              <div className="setting-item full-width">
                <h3 style={{ margin: "1.5rem 0 1rem 0", color: "#374151" }}>
                  Account Information
                </h3>
              </div>

              <div className="setting-item">
                <label>Account Identifier</label>
                <div className="form-input-readonly">
                  {transaction.accountIdentifier || "Not specified"}
                </div>
              </div>

              <div className="setting-item">
                <label>Terminal Identifier</label>
                <div className="form-input-readonly">
                  {transaction.terminalIdentifier || "Not specified"}
                </div>
              </div>

              {/* Error Information */}
              {transaction.errorText && (
                <>
                  <div className="setting-item full-width">
                    <h3 style={{ margin: "1.5rem 0 1rem 0", color: "#374151" }}>
                      Error Information
                    </h3>
                  </div>
                  <div className="setting-item full-width">
                    <label>Error Details</label>
                    <div
                      className="form-input-readonly"
                      style={{
                        color: "#dc2626",
                        background: "#fef2f2",
                        border: "1px solid #fecaca",
                      }}
                    >
                      {transaction.errorText}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="slide-panel-footer">
            <button
              type="button"
              className="btn primary"
              onClick={handleClose}
              style={{ width: "100%" }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default TransactionDetails;
